import { formatMoney, type TeenInsights } from '@teentack/shared';
import { ParentShell } from '@/features/parent/ParentShell';
import { EmptyState, StatTile } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { useInsights } from '@/features/parent/use-parent-overview';
import { StarRating } from '@/features/reviews/Stars';
import { Badge } from '@/components/ui/Badge';
import { ErrorNote } from '@/components/Form';
import { BookIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';

/**
 * How it is going. [Parent step 23]
 *
 * Deliberately not a dashboard of everything measurable — four numbers a
 * guardian would actually repeat out loud, the categories their teen keeps
 * choosing, and a plain bar chart of the last year.
 */

/** "2026-09" → "Sep". */
function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'short' });
}

function MonthlyChart({ insights }: { insights: TeenInsights }) {
  const months = insights.months.slice(-12);
  if (months.length === 0) return null;

  // Scaled against the best month so a quiet month still reads as small
  // rather than being flattened against an arbitrary axis.
  const peak = Math.max(...months.map((entry) => entry.earnedMinor), 1);

  return (
    <div className="mt-5">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
        Earned by month
      </p>
      <div className="mt-3 flex items-end gap-1.5 overflow-x-auto pb-1" style={{ height: 96 }}>
        {months.map((entry) => {
          const height = Math.max(2, Math.round((entry.earnedMinor / peak) * 76));
          return (
            <div key={entry.month} className="flex min-w-8 flex-1 flex-col items-center gap-1">
              <div
                className="w-full rounded-t bg-brand-500/80"
                style={{ height }}
                title={`${entry.month}: ${formatMoney(entry.earnedMinor, insights.currency)} across ${entry.jobsCompleted} ${entry.jobsCompleted === 1 ? 'job' : 'jobs'}`}
              />
              <span className="text-[0.65rem] font-medium text-slate-400">
                {monthLabel(entry.month)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeenCard({ insights }: { insights: TeenInsights }) {
  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-ink">{insights.teenName}</h2>
        {insights.averageRating !== null && (
          <span className="inline-flex items-center gap-2">
            <StarRating value={insights.averageRating} />
            <span className="text-sm font-bold text-ink">
              {insights.averageRating.toFixed(1)}
            </span>
            <span className="text-xs text-slate-400">
              ({insights.reviewCount} {insights.reviewCount === 1 ? 'review' : 'reviews'})
            </span>
          </span>
        )}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Earned"
          value={formatMoney(insights.totalEarnedMinor, insights.currency)}
          hint="Released payments only"
          tone={insights.totalEarnedMinor > 0 ? 'good' : 'neutral'}
        />
        <StatTile label="Jobs finished" value={insights.jobsCompleted} hint="Completed or approved" />
        <StatTile
          label="Employers"
          value={insights.employersWorkedFor}
          hint="Different people they have worked for"
        />
        <StatTile
          label="Reviews"
          value={insights.reviewCount}
          hint={insights.reviewCount === 0 ? 'Not rated yet' : 'From employers'}
        />
      </div>

      {insights.topCategories.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            What they keep choosing
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {insights.topCategories.map((entry) => (
              <Badge key={entry.category} tone="neutral">
                {entry.category} · {entry.jobs}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <MonthlyChart insights={insights} />
    </section>
  );
}

export function InsightsPage() {
  const insights = useInsights();
  const teens = insights.data?.teens ?? [];

  return (
    <ParentShell
      title="How it is going"
      description="Earnings, jobs finished and how employers rate them — all from work that actually happened."
    >
      {insights.isError && (
        <ErrorNote message={presentApiError(insights.error, 'Could not load insights').message} />
      )}

      {insights.isPending && (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-56" />
        </div>
      )}

      {insights.data && teens.length === 0 && (
        <EmptyState icon={<BookIcon className="size-6" />} title="No approved teens yet">
          Once you approve a teen account and they finish some work, this fills in.
        </EmptyState>
      )}

      {teens.length > 0 && (
        <div className="space-y-6">
          {teens.map((entry) => (
            <TeenCard key={entry.teenId} insights={entry} />
          ))}
        </div>
      )}
    </ParentShell>
  );
}
